import { statusOf, TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'

/** How a change is written, in one file (plan 1-3, fixed rules).
 *
 *  - A rank's direction comes from its `status` / change `kind` only, never
 *    from the sign of `delta`: the sign means "climbed" on one type and "rank
 *    number grew" on another. ▲ n (delta-up) / ▼ n (delta-down) / · (same),
 *    n the absolute number of places. Ranks carry no + or −.
 *  - Quantities (sessions, users, purchases): ▲ more, ▼ fewer.
 *  - Popularity and difficulty are not ours to win: "72 → 58", muted.
 *  - Top-10 entries and exits are counts, so they carry signs: +2 −1.
 *
 *  Colour sits on the label only; a sparkline beside it stays one colour. */

const UP = '▲'
const DOWN = '▼'

export interface DeltaProps {
  /** The move: up · down · same · new · lost · out, or a change kind
   *  (rank_up, rank_down, rank_new, rank_lost). */
  status: string
  /** Places moved. Its sign is ignored. */
  delta?: number
  /** Rank before and after; shown as "#12 → #8" when given. 0 = not ranked. */
  before?: number
  after?: number
  className?: string
}

function rankText(r: number | undefined): string {
  return r && r > 0 ? `#${r}` : '—'
}

function direction(status: string): 'up' | 'down' | 'same' | 'other' {
  if (status === 'up' || status === 'rank_up') return 'up'
  if (status === 'down' || status === 'rank_down') return 'down'
  if (status === 'same') return 'same'
  return 'other'
}

/** The ranks beside the mark. A new keyword has no rank before, so it reads
 *  "신규 #30", not "신규 — → #30"; a lost one reads "이탈 #12 → 순위 밖". The
 *  rest keep "#12 → #8". */
function ranksText(status: string, before: number | undefined, after: number | undefined): string | null {
  if (status === 'new' || status === 'rank_new') return after && after > 0 ? `#${after}` : null
  if (status === 'lost' || status === 'rank_lost') return before && before > 0 ? `#${before} → 순위 밖` : '순위 밖'
  return `${rankText(before)} → ${rankText(after)}`
}

/** A rank change: ▲ 4 · ▼ 4 · · · 신규 · 이탈 · 순위 밖, then "#12 → #8". */
export function Delta({ status, delta, before, after, className }: DeltaProps) {
  const dir = direction(status)
  const n = Math.abs(delta ?? (before && after ? after - before : 0))
  const def = statusOf('move', status)
  let mark
  if (dir === 'up') {
    mark = (
      <span className="font-semibold text-delta-up">
        <span aria-hidden>{UP}</span> {n}
      </span>
    )
  } else if (dir === 'down') {
    mark = (
      <span className="font-semibold text-delta-down">
        <span aria-hidden>{DOWN}</span> {n}
      </span>
    )
  } else if (dir === 'same') {
    mark = (
      <span aria-hidden className="text-muted-foreground">
        ·
      </span>
    )
  } else {
    mark = <span className={cn('font-medium', TONE_CLASS[def.tone].text, def.dim && 'opacity-60')}>{def.label}</span>
  }
  const ranks = before !== undefined || after !== undefined ? ranksText(status, before, after) : null
  return (
    <span className={cn('inline-flex items-baseline gap-1.5 whitespace-nowrap tabular-nums', className)}>
      {/* The arrow is drawn; the reader hears the word. */}
      {(dir === 'up' || dir === 'down' || dir === 'same') && <span className="sr-only">{def.label}</span>}
      {mark}
      {ranks && <span className="text-muted-foreground">{ranks}</span>}
    </span>
  )
}

/** A quantity's change against the period before: ▲ 12 more, ▼ 3 fewer,
 *  nothing when equal or when there is no period before. */
export function DeltaCount({ now, before, className }: { now: number; before?: number | null; className?: string }) {
  if (before === undefined || before === null) return null
  const d = now - before
  if (d === 0) return null
  const up = d > 0
  return (
    <span className={cn('inline-flex items-baseline gap-1 whitespace-nowrap font-semibold tabular-nums', up ? 'text-delta-up' : 'text-delta-down', className)}>
      <span className="sr-only">{up ? '증가' : '감소'}</span>
      <span aria-hidden>{up ? UP : DOWN}</span>
      {Math.abs(d).toLocaleString('ko-KR')}
    </span>
  )
}

/** A measure that moved but is not a result of ours: "72 → 58", muted. */
export function Shift({ before, after, className }: { before: number; after: number; className?: string }) {
  return (
    <span className={cn('whitespace-nowrap tabular-nums text-muted-foreground', className)}>
      {before} → {after}
    </span>
  )
}

/** Top-10 entries and exits as counts: "+2 −1"; a zero side is left out. */
export function EntryExit({ entered, exited, className }: { entered: number; exited: number; className?: string }) {
  if (entered <= 0 && exited <= 0) return <span className={cn('text-muted-foreground', className)}>—</span>
  return (
    <span className={cn('inline-flex items-baseline gap-1.5 whitespace-nowrap tabular-nums', className)}>
      {entered > 0 && (
        <span className="text-delta-up" title={`상위 10 진입 ${entered}`}>
          +{entered}
        </span>
      )}
      {exited > 0 && (
        <span className="text-delta-down" title={`상위 10 이탈 ${exited}`}>
          −{exited}
        </span>
      )}
    </span>
  )
}
